import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LangsProvider } from '../../../common/providers/langs-provider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from '../../../common/providers/current-user';
import { AthletesProvider } from '../../../athletes/providers/athletes-provider';
import { TeamRow, TeamsProvider } from '../../providers/teams-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions, MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Team } from "../../../common/models/team";
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { Lang } from "../../../common/models/lang";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { MatOption } from "@angular/material/autocomplete";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatFormField } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatSelect } from "@angular/material/select";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { take } from "rxjs/operators";

@Component({
  selector: 'teams-athletes-form',
  providers: [],
  styleUrls: ['./athletes.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    TranslateModule,
    MatDialogContent,
    MatDialogActions,
    MatOption,
    MatCheckbox,
    MatFormField,
    MatIcon,
    MatProgressBar,
    MatSelect,
    ReactiveFormsModule,
    NgForOf,
    FormsModule,
    MatButton,
    MatDialogClose,
    NgIf,
    MatIconButton,
    MatTooltip,
    AsyncPipe,
    NgxMatSelectSearchModule
  ],
  templateUrl: './athletes.html'
})
export class TeamsAthletesFormComponent implements OnInit, AfterViewInit, OnDestroy {
    public isLoading = false;
    public formTitle = '';
    public confirmButtonTitle = 'Save';
    public availableTeams: Team[] = [];
    public organisation: Organization = {} as Organization;
    public teamRow: TeamRow = {} as TeamRow;
    public teamId = 0;
    public langId: number = Lang.ID_DEFAULT;
    public removeCoachAthleteRelations = true;
    public removeGuestAthleteRelations = true;

    @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;
    public availableAthletes: User[] = [];
    public athletesSelected: User[] = [];
    public athletesSelectFormControl: FormControl<User[]> = new FormControl<User[]>([]) as FormControl<User[]>;
    public athleteFilterFormControl: FormControl<string> = new FormControl<string>('') as FormControl<string>;
    public filteredAthletes: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);
    protected _onDestroy = new Subject<void>();

    constructor(
        public conf: ConfigProvider,
        public currentUser: CurrentUser,
        public utils: UtilsProvider,
        public dialog: MatDialogRef<TeamsAthletesFormComponent>,
        public usersProvider: UsersProvider,
        private _toaster: MatSnackBar,
        private _title: Title,
        private _athletesProvider: AthletesProvider,
        private _router: Router,
        private _route: ActivatedRoute,
        // private _dateAdapter: DateAdapter<any>,
        private _langsProvider: LangsProvider,
        private _translater: TranslateService,
        private _teamProvider: TeamsProvider,
        @Inject(MAT_DIALOG_DATA) private _data : any
    ) {

        this.availableAthletes = _data.availableAthletes;
        this.organisation = _data.organization;

        if (_data.teamRow) {

            this.teamRow = this._data.teamRow;
            this.teamId = this._data.teamRow.team.id;
            this.athletesSelected = this.teamRow.athletes;
        }

        this.formTitle = this._data.formTitle;
        this.confirmButtonTitle = this._data.confirmButtonTitle;
    }

    public ngOnInit() {
      // Charger les coachs filtrés initialement
      this.filteredAthletes.next(this.availableAthletes.slice());

      // Écouter les changements dans le champ de recherche
      this.athleteFilterFormControl.valueChanges
        .pipe(takeUntil(this._onDestroy))
        .subscribe(() => {
          this.filterAthletes();
        });

      // Initialiser les coachs sélectionnés si nécessaire
      if (this.athletesSelected.length > 0) {
        this.athletesSelectFormControl.setValue(
          this.athletesSelected
        );
      }
    }

    public ngOnDestroy() {
      this._onDestroy.next();
      this._onDestroy.complete();
    }

    public ngAfterViewInit() {
      this.setInitialValue();
    }

    public onSaveClicked(): void {
        if (this.canSave()) {
            this.isLoading = true;
            this.save().subscribe({
                next: (result: TeamRow) => {
                    this._toaster.open(this._translater.instant('Team_saved'), '', {duration: 5000});
                    this.isLoading = false;
                    this.dialog.close(result);
                },
                error: (error: any) => this.onError(error)
            });
        } else {
            this.touchFields();
            this._toaster.open(this._translater.instant('Correct_form_errors'), '', {duration: 5000});
        }
    }

    public canSave(): boolean {
        return !this.athletesSelectFormControl.invalid;
    }

    private touchFields(): void {
        this.athletesSelectFormControl.markAsTouched();
    }

    private save(): Observable<TeamRow> {
        const teamRow: TeamRow = {
            team: this.teamRow.team,
            coaches: [],
            athletes: [],
            coachNames: '',
        };

        // user (athlete)
        if (this.athletesSelectFormControl.value.length) {
            for (let i = 0; i < this.athletesSelectFormControl.value.length; i++) {
                const athlete = this.utils.findIn(
                    this.athletesSelectFormControl.value[i].id,
                    this.availableAthletes,
                    'id');
                if (athlete) {
                    teamRow.athletes.push(athlete);
                }
            }
        }

        return this._teamProvider.saveTeamAthletes(this.organisation.id!, this.removeCoachAthleteRelations, this.removeGuestAthleteRelations, teamRow)
    }

  protected setInitialValue() {
    this.filteredAthletes
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: User, b: User) => a && b && a.id === b.id;
      });
  }

  protected filterAthletes() {
    if (!this.availableAthletes) {
      return;
    }
    // get the search keyword
    let search = this.athleteFilterFormControl.value;
    if (!search) {
      this.filteredAthletes.next(this.availableAthletes.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredAthletes.next(
      this.availableAthletes.filter(athlete => `${athlete.firstName} ${athlete.lastName}`.toLowerCase().indexOf(search) > -1)
    );
  }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 422) {
            if (error.error.errors && error.error.errors.email) {
                // c'est qu'on a essayé de faire valider un email test@test par exemple (c'est validé par le validator angular mais pas laravel...)
                return;
            }
        }

        this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
    }

}
